import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
} from "@mui/material";
import ClosIcon from "src/assets/svg-icons/close.svg";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "src/firebase";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { IOperatorTracking } from "src/types/employees";
import moment from "moment";

interface IProps {
  open: boolean;
  onClose: any;
  id: string;
}

const containerStyle = { height: "600px", width: "100%" };

const OperatorPathMap: React.FC<IProps> = ({ open, onClose, id }) => {
  const [trackData, setTrackData] = useState<IOperatorTracking>();
  const [isLoadingFetching, setIsLoadingFetching] = useState(true);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selectedPoint, setSelectedPoint] = useState<{
    lat: number;
    lng: number;
    time: number;
  } | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBjGZRBq1seDBmbq62CMyV2VkpRrOwNh_Q",
  });

  const pathPoints = (trackData?.trackingList ?? []).map((point) => {
    return { lat: point.latitude, lng: point.longitude, time: point.time };
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (!pathPoints || pathPoints.length === 0) return;

      const bounds = new window.google.maps.LatLngBounds();
      pathPoints.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);
    },
    [pathPoints],
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Fetch path data
  useEffect(() => {
    if (id) {
      let unsubscribe: () => void;
      const docRef = doc(db, "shifts", `${id}`);

      unsubscribe = onSnapshot(docRef, (doc) => {
        if (doc.exists()) {
          const data = { ...doc.data() };
          setTrackData(data as IOperatorTracking);
          setIsLoadingFetching(false);
          console.log({ data });
        } else {
          console.log("No such document!");
          setNotifications((prev) => [
            ...prev,
            {
              type: "error",
              message: "No such employee!",
            },
          ]);
        }
      });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [id]);

  // recompute map layout after dialog opens
  useEffect(() => {
    if (open && isLoaded && mapRef.current && pathPoints.length) {
      const map = mapRef.current;

      // Fix: force map to recalc size
      window.google.maps.event.trigger(map, "resize");

      // Re-fit bounds
      const bounds = new google.maps.LatLngBounds();
      pathPoints.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);
    }
  }, [open, isLoaded, pathPoints]);

  if (!isLoaded) return null;

  const start = pathPoints[0];
  const end = pathPoints[pathPoints.length - 1];

  const handleClose = () => {
    onClose();
  };

  console.log("Google map loaded:", isLoaded, pathPoints, trackData);

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"md"}
      open={open}
      onClose={handleClose}
      component={"div"}
      data-testid="location-form-service-dialog"
    >
      <Box borderRadius={2} overflow="hidden" position={"relative"}>
        {isLoadingFetching ? (
          <Box
            height={"400px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box position={"relative"}>
            <IconButton
              size="small"
              sx={{
                ml: "auto",
                position: "absolute",
                top: 2,
                right: 2,
                zIndex: 2,
              }}
              onClick={handleClose}
            >
              <img src={ClosIcon} alt="" />
            </IconButton>
            <GoogleMap
              mapContainerStyle={containerStyle}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                mapTypeId: "satellite",
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: false,
              }}
            >
              {/* Draw employee path */}
              <Polyline
                path={pathPoints}
                options={{
                  strokeColor: "#1976d2",
                  strokeOpacity: 0.7,
                  strokeWeight: 3,
                }}
              />

              {/* Start marker */}
              {/* {start && pathPoints.length > 1 && (
                <Marker
                  position={start}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: "#1976d2",
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                  }}
                  label={{
                    text: " ",
                    color: "#115f1fff",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                />
              )} */}

              {pathPoints.length > 1 &&
                pathPoints?.map((point, index) => {
                  if (index === pathPoints.length - 1) return null;
                  return (
                    <Marker
                      key={point.time}
                      position={{
                        lat: point.lat,
                        lng: point.lng,
                      }}
                      onClick={() => setSelectedPoint(point)}
                      title={moment(point.time).format("DD MMM, YYYY hh:mm A")}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 6,
                        fillColor: "#1976d2",
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                      }}
                      label={{
                        text: " ",
                        color: "#115f1fff",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    />
                  );
                })}

              {/* End marker */}
              {end && (
                <Marker
                  position={{ lat: end.lat, lng: end.lng }}
                  onClick={() => setSelectedPoint(end)}
                  icon={{
                    url: "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2_hdpi.png",
                    scaledSize: new window.google.maps.Size(27, 43),
                    labelOrigin: new window.google.maps.Point(13, -10),
                  }}
                  label={{
                    text: "Current Location",
                    color: "#e55640ff",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                />
              )}

              {selectedPoint && (
                <InfoWindow
                  position={{
                    lat: selectedPoint.lat,
                    lng: selectedPoint.lng,
                  }}
                  onCloseClick={() => setSelectedPoint(null)}
                >
                  <Box p={1}>
                    {moment(selectedPoint.time).format("DD MMM, YYYY hh:mm A")}
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default OperatorPathMap;
